import { getMessaging, getToken, deleteToken, onMessage } from 'firebase/messaging';
import api from '../seller-apis/api';
import { meOnly } from '../seller-apis/apidataField';

const VAPID_KEY = 'BFYNJ5lUq382fGKPlSiHOkTI24Q8dD6BjSgb5yGGDAKY1dZof2crdxhFIa9HJSVimF386gzAALH6h42uY2SatnQ';
const TOKEN_REFRESH_INTERVAL = 1 * 60 * 60 * 1000; // 24 hours
const TOKEN_STORAGE_KEY = 'fcmToken';
const LAST_REFRESH_KEY = 'fcmTokenLastRefresh';

/**
 * Updates FCM token on the server
 * @param {string} newToken - The new FCM token
 * @returns {Promise<boolean>} - Success status
 */
const updateTokenOnServer = async (newToken) => {
  try {
    const res = await api.auth.me(meOnly({ "excludeField": { "fcm_tokens": 1, "active": 1 } }));
    let tokens = res.data.fcm_tokens || [];
    
    // Remove old token if exists and add new token
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (storedToken && tokens.includes(storedToken)) {
      tokens = tokens.filter(token => token !== storedToken);
    }
    
    // Add new token if not already present
    if (!tokens.includes(newToken)) {
      tokens.push(newToken);
    }
    
    await api.seller.updateInfo({ fcm_tokens: tokens });
    
    // Update local storage
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());
    
    return true;
  } catch (err) {
    console.error("Error updating FCM token on server:", err);
    return false;
  }
};

/**
 * Removes FCM token from server and local storage
 * @returns {Promise<boolean>} - Success status
 */
export const removeFCMToken = async () => {
  try {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    
    if (storedToken) {
      // Remove from server
      const res = await api.auth.me(meOnly({ "excludeField": { "fcm_tokens": 1, "active": 1 } }));
      let tokens = res.data.fcm_tokens || [];
      tokens = tokens.filter(token => token !== storedToken);
      
      await api.seller.updateInfo({ fcm_tokens: tokens });
    }
    
    // Delete token from Firebase
    const messaging = getMessaging();
    await deleteToken(messaging);
    
    // Clear local storage
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(LAST_REFRESH_KEY);
    
    return true;
  } catch (err) {
    console.error("Error removing FCM token:", err);
    // Clear local storage even if server update fails
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(LAST_REFRESH_KEY);
    return false;
  }
};

/**
 * Requests and updates FCM token
 * This function is called on every app start and checks if token needs refresh
 * based on the last refresh timestamp (24 hours). This ensures tokens are refreshed
 * even if the app was closed for extended periods.
 * 
 * @param {boolean} forceRefresh - Force refresh even if token exists
 * @returns {Promise<string|null>} - The FCM token or null
 */
export const requestFCMToken = async (forceRefresh = false) => {
  // Check if user is logged in
  if (!localStorage.getItem("token")) {
    console.log('User not logged in, skipping FCM token request');
    return null;
  }

  // Check if notification permission is granted
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return null;
  }

  try {
    const messaging = getMessaging();
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

    // Check if we need to refresh based on last refresh timestamp
    // This is crucial for client apps that may be closed for days
    const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY);
    const timeSinceRefresh = lastRefresh ? (Date.now() - parseInt(lastRefresh)) : Infinity;
    const needsRefresh = forceRefresh || 
                        !storedToken || 
                        !lastRefresh || 
                        timeSinceRefresh > TOKEN_REFRESH_INTERVAL;

    if (!needsRefresh) {
      const hoursOld = Math.floor(timeSinceRefresh / (1000 * 60 * 60));
      return storedToken;
    }

    const reason = !storedToken ? 'no token stored' :
                   !lastRefresh ? 'no refresh timestamp' :
                   timeSinceRefresh > TOKEN_REFRESH_INTERVAL ? 'token older than 24 hours' :
                   'forced refresh';
    
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
    });

    if (currentToken) {
      
      // Only update if token has changed or it's a forced refresh
      if (currentToken !== storedToken || forceRefresh) {
        await updateTokenOnServer(currentToken);
      } else {
        // Just update the timestamp
        localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());
      }
      
      return currentToken;
    } else {
      console.error('No FCM token available. Request permission to generate one.');
      return null;
    }
  } catch (err) {
    console.error('Error retrieving FCM token:', err);
    
    // If token is invalid, clear it
    if (err.code === 'messaging/token-subscribe-failed' || 
        err.code === 'messaging/invalid-vapid-key') {
      await removeFCMToken();
    }
    
    return null;
  }
};

/**
 * Validates current FCM token and refreshes if needed
 * @returns {Promise<boolean>} - Whether token is valid
 */
export const validateFCMToken = async () => {
  const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  
  if (!storedToken) {
    const token = await requestFCMToken(true);
    return !!token;
  }

  try {
    // Try to get current token
    const messaging = getMessaging();
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
    });

    // If tokens don't match, update
    if (currentToken !== storedToken) {
      await updateTokenOnServer(currentToken);
      return true;
    }

    return true;
  } catch (err) {
    console.error('FCM token validation failed:', err);
    
    // Clear invalid token
    await removeFCMToken();
    
    // Try to get a new token
    const newToken = await requestFCMToken(true);
    return !!newToken;
  }
};

/**
 * Sets up periodic token validation
 * @param {number} interval - Validation interval in milliseconds (default: 24 hours)
 * @returns {Function} - Cleanup function to stop validation
 */
export const setupTokenValidation = (interval = TOKEN_REFRESH_INTERVAL) => {
  
  // Validate immediately
  validateFCMToken();
  
  // Set up periodic validation
  const intervalId = setInterval(() => {
    validateFCMToken();
  }, interval);

  // Return cleanup function
  return () => {
    clearInterval(intervalId);
  };
};

/**
 * Sets up FCM message listener for foreground notifications
 * @param {Function} onMessageCallback - Callback function for messages
 * @returns {Function} - Cleanup function
 */
export const setupMessageListener = (onMessageCallback) => {
  try {
    const messaging = getMessaging();
    
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('FCM message received:', payload);
      if (onMessageCallback) {
        onMessageCallback(payload);
      }
    });

    return unsubscribe;
  } catch (err) {
    console.error('Error setting up message listener:', err);
    return () => {};
  }
};

/**
 * Checks if FCM token needs refresh based on last refresh time
 * @returns {boolean} - Whether refresh is needed
 */
export const needsTokenRefresh = () => {
  const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY);
  const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  
  if (!storedToken || !lastRefresh) {
    return true;
  }
  
  const timeSinceRefresh = Date.now() - parseInt(lastRefresh);
  return timeSinceRefresh > TOKEN_REFRESH_INTERVAL;
};

/**
 * Gets the current FCM token from localStorage
 * @returns {string|null} - The stored FCM token
 */
export const getCurrentToken = () => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

export default {
  requestFCMToken,
  validateFCMToken,
  setupTokenValidation,
  setupMessageListener,
  removeFCMToken,
  needsTokenRefresh,
  getCurrentToken
};

